import { Group, Stack, Text, Tooltip } from '@mantine/core';

import { formatMoney } from '@/utils';
import classes from './ContractMeter.module.css';

/** Consumption at or past this share of the commitment shows as a warning. */
export const WARNING_SHARE = 0.8;

export type MeterState = 'within' | 'nearing' | 'over';

export function meterState(consumed: number, commitment: number): MeterState {
  if (consumed > commitment) {
    return 'over';
  }
  return commitment > 0 && consumed / commitment >= WARNING_SHARE ? 'nearing' : 'within';
}

export interface ContractMeterProps {
  commitment: number;
  consumed: number;
  currency: string | null;
  /** Where the crossing item began, as an amount consumed before it. */
  crossingAt?: number | null;
  /** Hide the text line under the bar (a table row shows it elsewhere). */
  compact?: boolean;
}

function percent(part: number, whole: number): string {
  return `${(whole > 0 ? (part / whole) * 100 : 0).toFixed(4)}%`;
}

/**
 * What a contract has drawn against its commitment. Scaled to the larger of
 * the two, so an overage shows as the part past the commitment mark. State is
 * carried by text as well as colour: never colour alone.
 */
export function ContractMeter({
  commitment,
  consumed,
  currency,
  crossingAt = null,
  compact = false,
}: Readonly<ContractMeterProps>) {
  const state = meterState(consumed, commitment);
  const scale = Math.max(commitment, consumed, 0);
  const within = Math.min(consumed, commitment);
  const overage = Math.max(consumed - commitment, 0);
  const money = (value: number) => formatMoney(value, currency) ?? String(value);
  const summary = `${money(consumed)} of ${money(commitment)}`;
  const stateText =
    state === 'over'
      ? `Over by ${money(overage)}`
      : `${money(Math.max(commitment - consumed, 0))} remaining`;

  return (
    <Stack gap={4}>
      <Tooltip label={`${summary} · ${stateText}`} withArrow>
        <div
          className={`${classes.meter} ${state === 'over' ? classes.full : ''}`}
          role="meter"
          aria-label={`Consumed ${summary}. ${stateText}.`}
          aria-valuemin={0}
          aria-valuemax={commitment}
          aria-valuenow={consumed}
        >
          {within > 0 ? (
            <div
              className={`${classes.segment} ${
                state === 'nearing' ? classes.withinWarning : classes.withinNormal
              } ${overage > 0 ? '' : classes.dataEnd}`}
              style={{ width: percent(within, scale) }}
            />
          ) : null}
          {overage > 0 ? (
            <div
              className={`${classes.segment} ${classes.overage} ${classes.dataEnd}`}
              style={{ width: percent(overage, scale) }}
            />
          ) : null}
          {state === 'over' ? (
            <div
              className={classes.commitmentMark}
              style={{ left: percent(commitment, scale) }}
              data-testid="commitment-mark"
            />
          ) : null}
          {crossingAt !== null && crossingAt > 0 && crossingAt < scale ? (
            <div
              className={classes.crossingMark}
              style={{ left: percent(crossingAt, scale) }}
              data-testid="crossing-mark"
            />
          ) : null}
        </div>
      </Tooltip>
      {compact ? null : (
        <Group justify="space-between" gap="xs">
          <Text size="sm">{summary}</Text>
          <Text
            size="sm"
            fw={state === 'over' ? 600 : 400}
            c={state === 'over' ? undefined : 'dimmed'}
          >
            {stateText}
          </Text>
        </Group>
      )}
    </Stack>
  );
}
